"use client";

import {useState} from "react";
import {Star} from "lucide-react";
import {
    Carousel,
    CarouselContent,
    CarouselItem,
    CarouselNext,
    CarouselPrevious,
    type CarouselApi,
} from "@/components/ui/carousel";
import {CarouselDots, useAutoplayPlugins} from "@/components/carousel-extras";
import type {Testimonial} from "@/lib/public-data";

/** Testimonials carousel; the data is fetched (and cached) on the server by the page. */
export default function Testimonials({data}: {data: Testimonial[]}) {

    const [api, setApi] = useState<CarouselApi>();
    const plugins = useAutoplayPlugins(10000);
    // Drop entries with nothing to show (no highlight, quote or valid rating); rating-only ones stay.
    const visible = data.filter(
        (item) =>
            item.highlight?.trim() ||
            item.quote?.trim() ||
            (Number.isInteger(item.rating) && item.rating! >= 1 && item.rating! <= 5),
    );
    return (
        <div
            id="testimonials"
            className="bg-secondary pb-20 scroll-m-20 text-white"
        >
            <h2 className="text-4xl text-center p-5">Client Experience</h2>
            <hr className="w-3/4 m-auto"></hr>
            <Carousel setApi={setApi} plugins={plugins} opts={{loop: true}} aria-label="Client testimonials">
                <CarouselContent className="ml-0">
                    {visible.map((item: Testimonial) => {
                    const highlight = item.highlight?.trim() || null;
                    const quote = item.quote?.trim() || null;
                    const rating = Number.isInteger(item.rating) && item.rating! >= 1 && item.rating! <= 5 ? item.rating! : null;
                    // The highlight leads; without one the quote is the main text.
                    const lead = highlight ?? quote;
                    const excerpt = highlight ? quote : null;
                    return (
                        <CarouselItem key={item.id ?? item.name} className="pl-0 px-14 md:px-20">
                        <figure className="mt-10 mx-auto block w-full max-w-4xl pb-8">
                            {rating !== null && (
                                <div role="img" aria-label={`Rated ${rating} out of 5`} className="flex justify-center gap-1 mb-4">
                                    {[1, 2, 3, 4, 5].map((n) => (
                                        <Star
                                            key={n}
                                            aria-hidden="true"
                                            className={n <= rating ? "size-6 fill-amber-400 text-amber-400" : "size-6 text-stone-900"}
                                        />
                                    ))}
                                </div>
                            )}
                            {lead && (
                                <blockquote className="italic font-serif text-center text-2xl md:text-3xl md:leading-relaxed">
                                    &ldquo;{lead}&rdquo;
                                </blockquote>
                            )}
                            {excerpt && (
                                <p className="mt-5 text-center text-base md:text-lg text-stone-200 md:leading-loose">{excerpt}</p>
                            )}
                            <figcaption className="text-center text-lg pt-5">{item.name}</figcaption>
                        </figure>
                        </CarouselItem>
                    );
                })}
                </CarouselContent>
                <CarouselPrevious className="left-2 md:left-6 text-foreground"/>
                <CarouselNext className="right-2 md:right-6 text-foreground"/>
            </Carousel>
            <CarouselDots api={api} className="relative z-10 -mt-6"/>
        </div>
    );
}
